import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress
} from "@mui/material";
import { Add, Edit, Delete, Search } from "@mui/icons-material";
import {
  collection, query, where, getDocs, addDoc, updateDoc,
  deleteDoc, doc, Timestamp, orderBy, getDoc
} from "firebase/firestore";
import { db, auth } from "../../firebase"; // Adjust paths to match your project structure
import Swal from "sweetalert2";

function Daybooknote() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    title: "",
    note: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    try {
      if (!auth.currentUser) return;
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data()?.schoolId;
      setSchoolId(sId);
      if (sId) {
        await fetchRecords(sId);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error("Initialization Error:", e);
      setLoading(false);
    }
  };

  const fetchRecords = async (sId) => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "daybooknotes"),
        where("schoolId", "==", sId),
        orderBy("date", "desc")
      );
      const snap = await getDocs(q);
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error fetching daybook notes:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date  || !formData.note) {
      return Swal.fire("Error", "All fields are required", "error");
    }

    setSubmitting(true);
    try {
      const payload = {
        date: formData.date,
        title: formData.title.trim(),
        note: formData.note.trim(),
        schoolId,
        updatedAt: Timestamp.now(),
        createdAt: editId ? formData.createdAt || Timestamp.now() : Timestamp.now()
      };

      if (editId) {
        await updateDoc(doc(db, "daybooknotes", editId), payload);
        Swal.fire("Success", "Note updated successfully", "success");
      } else {
        await addDoc(collection(db, "daybooknotes"), payload);
        Swal.fire("Success", "Note added successfully", "success");
      }

      setOpen(false);
      resetForm();
      if (schoolId) fetchRecords(schoolId);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Delete Note?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete"
    });

    if (res.isConfirmed) {
      try {
        await deleteDoc(doc(db, "daybooknotes", id));
        if (schoolId) fetchRecords(schoolId);
        Swal.fire("Deleted", "Note has been removed.", "success");
      } catch (e) {
        Swal.fire("Error", e.message, "error");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      title: "",
      note: ""
    });
    setEditId(null);
  };

  const filteredRecords = records.filter(rec =>
    rec.title?.toLowerCase().includes(search.toLowerCase()) ||
    rec.note?.toLowerCase().includes(search.toLowerCase()) ||
    rec.date?.includes(search)
  );

  return (
    <Box p={1} bgcolor="#f8fafc" minHeight="100vh">
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="primary.dark" sx={{ lineHeight: 1.2 }}>
            Daybook Notes
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Manage daily logs, operational notes, and essential reminders
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          size="small"
          onClick={() => { resetForm(); setOpen(true); }}
          sx={{ borderRadius: 1.5, px: 2, height: 32, fontSize: '13px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          Add Note
        </Button>
      </Stack>

      {/* Search Bar */}
      <Card sx={{ p: 1, mb: 1.5, borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by title, note, or date (YYYY-MM-DD)..."
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            sx: { borderRadius: 1.5, height: 36, fontSize: '13px' }
          }}
        />
      </Card>

      {/* Compact Data Grid Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#eff6ff" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px', width: '100px' }}>Sr No</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px', width: '110px' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '13px' }}>Note</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 1, fontSize: '13px', width: '100px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2 }}><CircularProgress size={20} /></TableCell></TableRow>
            ) : filteredRecords.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, fontSize: '13px' }}>No notes found.</TableCell></TableRow>
            ) : (
              filteredRecords.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((rec, index) => (
                <TableRow key={rec.id} hover>
                  <TableCell sx={{ py: 0.8, fontSize: '12.5px' }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={{ py: 0.8, fontSize: '12.5px', whiteSpace: 'nowrap' }}>
                    {rec.date}
                  </TableCell>
                  <TableCell sx={{ py: 0.8, fontSize: '12.5px', color: 'text.secondary' }}>
                    {rec.note}
                  </TableCell>
                  <TableCell align="center" sx={{ py: 0.8 }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton
                        onClick={() => {
                          setEditId(rec.id);
                          setFormData({ ...rec });
                          setOpen(true);
                        }}
                        size="small"
                        color="primary"
                        sx={{ p: 0.5 }}
                      >
                        <Edit sx={{ fontSize: '16px' }} />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(rec.id)} size="small" color="error" sx={{ p: 0.5 }}>
                        <Delete sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ ".MuiTablePagination-toolbar": { minHeight: 36 }, ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: '12px' } }}
        />
      </TableContainer>

      {/* Entry / Edit Dialog Form */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, p: 2, fontSize: '16px' }}>
          {editId ? "Update Note" : "Add New Note"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Date"
              type="date"
              fullWidth
              value={formData.date}
              onChange={e => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{ shrink: true, style: { fontSize: 13 } }}
              inputProps={{ style: { fontSize: 13 } }}
            />

            <TextField
              size="small"
              label="Note"
              placeholder="Type your notes or details here..."
              multiline
              rows={3}
              fullWidth
              value={formData.note}
              onChange={e => setFormData({ ...formData, note: e.target.value })}
              inputProps={{ style: { fontSize: 13 } }}
              InputLabelProps={{ style: { fontSize: 13 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setOpen(false)} color="inherit" size="small" sx={{ fontSize: '13px' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            size="small"
            color="primary"
            sx={{ minWidth: 100, fontSize: '13px', height: 32 }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Daybooknote;